import { hash01 } from "./trailGeometry";
import { BLOOM_NAMES, LAYERS, makeTemplates, sampleCell } from "./flowerScatter";

/**
 * edgeFlowers — a lightweight cut-paper wildflower frame for the game screens.
 *
 * Reuses the meadow's primitives (the flower templates, the parallax LAYERS,
 * and the shared per-cell sampler) but NOT its path-bound placement. Instead of
 * clearing blooms away from a trail, it scatters them only in two vertical
 * bands hugging the LEFT and RIGHT screen edges, densest in the bottom corners
 * and thinning both inward and upward — so the play area is framed by flowers
 * without the home page's full meadow. Pure (no React/DOM) so it stays cheap
 * and testable, just like flowerScatter.js's buildFlowerField.
 */

// ---- tunables --------------------------------------------------------------
export const BAND = 160; // px each side band reaches in from its screen edge
export const H_POW = 1.6; // horizontal fade: higher => thins faster moving inward
export const V_POW = 2.5; // vertical fade: higher => flowers hug the bottom more tightly

// Scatter the three parallax layers over the viewport, keeping a flower only when
// it falls in an edge band, with a keep-probability that drops moving inward AND
// upward (densest in the bottom corners). Returns the viewBox size, the flower
// templates (baked shapes + extent pts), and per-layer placement instances — the
// same shape conventions as flowerScatter.js's buildFlowerField, so EdgeFlowers
// renders it the same way FlowerTurf renders the meadow.
export function buildEdgeFlowers({ width, height }) {
  const templates = makeTemplates();
  const layers = LAYERS.map((layer) => {
    const sp = layer.spacing;
    const cols = Math.ceil(width / sp);
    const lrows = Math.ceil(height / sp);
    const uses = [];
    for (let ix = 0; ix < cols; ix++) {
      for (let iy = 0; iy < lrows; iy++) {
        // One flower per cell, jittered within it (stable per cell via hash01).
        const c = sampleCell(ix, iy, layer, templates.length, BLOOM_NAMES.length);

        // Horizontal gate: only the two side bands; full at the edge, gone by
        // BAND. The last grid column can jitter past `width` — clamp so the
        // overshoot reads as "at the edge" (keeps the probability ≤ 1).
        const edgeDist = Math.max(0, Math.min(c.jx, width - c.jx));
        if (edgeDist >= BAND) continue;
        const hFactor = Math.pow(1 - edgeDist / BAND, H_POW);

        // Vertical weighting: dense across the bottom, thinning toward the top.
        // The last grid row can jitter below `height` — clamp so those cells
        // read as "at the bottom" (a negative base would make the pow NaN and
        // wave every bottom-row flower through both fades).
        const fromBottom = Math.max(0, height - c.jy) / height; // 0 at bottom .. 1 at top
        const vFactor = 1 - Math.pow(fromBottom, V_POW);

        // Keep with the combined probability (a stable per-cell hash, salt + 5,
        // independent of the sampler's streams).
        if (hash01(ix, iy, layer.salt + 5) > hFactor * vFactor) continue;

        uses.push({ x: c.jx, y: c.jy, sx: c.s * c.flip, sy: c.s, ti: c.ti, bi: c.bi });
      }
    }
    return uses;
  });

  return { vw: width, vh: height, templates, layers };
}
